/**
 * `@erase2d/fabric` declares `erasable` on Fabric's own FabricObjectProps. This
 * fully erased type-only import is what brings that declaration into the
 * program: without it `new Rect({ erasable: true })` and `toObject(['erasable'])`
 * are compile errors. It is a global `declare module`, so this one import covers
 * every file, and re-declaring it locally would conflict with the shipped one.
 */
import type {} from '@erase2d/fabric';
import { Ellipse, Group, Line, Rect, Triangle } from 'fabric';
import type { FabricObject, TPointerEvent } from 'fabric';

/**
 * Tool registry for the annotation canvas.
 *
 * This is the single description of what each tool does to the canvas. The
 * behaviour used to live in inline listeners inside `initCanvas`, which tangled
 * which tool was active with whether a drag was in progress with whether
 * history was replaying, and that tangle is where the accidental-duplicate-text
 * and broken-undo defects came from.
 */

export const TOOL = {
	MOVE: 'MOVE',
	PEN: 'PEN',
	TEXT: 'TEXT',
	SQUARE: 'SQUARE',
	CIRCLE: 'CIRCLE',
	ARROW: 'ARROW',
	ERASER: 'ERASER',
} as const;

export type ToolId = typeof TOOL[keyof typeof TOOL];

/** Toolbar order: creation tools sit between the two manipulation tools. */
export const TOOL_ORDER: ToolId[] = [
	TOOL.MOVE,
	TOOL.PEN,
	TOOL.TEXT,
	TOOL.SQUARE,
	TOOL.CIRCLE,
	TOOL.ARROW,
	TOOL.ERASER,
];

/**
 * Reserved at the right and bottom edges only. A box at the left or top edge is
 * fully usable because text flows right and down from its origin; a box started
 * at the right or bottom edge has nowhere to flow.
 */
export const TEXT_EDGE_CLAMP_PX = 50;

/**
 * The mdi-eraser mark, matching the toolbar button, as a cursor.
 *
 * White with a dark outline because the canvas sits over an arbitrary image and
 * a single-colour cursor disappears against half of them. The hotspot is the
 * eraser's rubber end rather than its centre, so the icon body sits up and to
 * the right of the pointer instead of covering the size-preview ring.
 */
const ERASER_ICON_PATH = 'M16.24,3.56L21.19,8.5C21.97,9.29 21.97,10.55 21.19,11.34L12,20.53C10.44,22.09 7.91,22.09 6.34,20.53L2.81,17C2.03,16.21 2.03,14.95 2.81,14.16L13.41,3.56C14.2,2.78 15.46,2.78 16.24,3.56M4.22,15.58L7.76,19.11C8.54,19.9 9.8,19.9 10.59,19.11L14.12,15.58L9.17,10.63L4.22,15.58Z';

/** The icon's own coordinate space, which the hotspot below is expressed in. */
const ERASER_ICON_VIEWBOX = 24;
/** Rendered cursor size. The only knob; the hotspot follows it. */
const ERASER_CURSOR_SIZE_PX = 18;
/** The rubber end of the mark, in viewBox units. */
const ERASER_HOTSPOT = { x: 4, y: 20 };

const toRenderedPx = (viewBoxUnits: number): number => {
	return Math.round(viewBoxUnits * ERASER_CURSOR_SIZE_PX / ERASER_ICON_VIEWBOX);
};

// Scaled up as the cursor shrinks, so the outline keeps roughly the same
// on-screen weight rather than thinning away at small sizes.
const eraserStrokeWidth: number = 1.1 * (ERASER_ICON_VIEWBOX / ERASER_CURSOR_SIZE_PX);

const eraserCursorSvg: string = [
	`<svg xmlns="http://www.w3.org/2000/svg"`,
	` width="${ERASER_CURSOR_SIZE_PX}" height="${ERASER_CURSOR_SIZE_PX}"`,
	` viewBox="0 0 ${ERASER_ICON_VIEWBOX} ${ERASER_ICON_VIEWBOX}">`,
	`<path d="${ERASER_ICON_PATH}" fill="#fff" stroke="#1a1a1a"`,
	` stroke-width="${eraserStrokeWidth.toFixed(2)}" stroke-linejoin="round"/>`,
	`</svg>`,
].join('');

// encodeURIComponent rather than base64: the markup stays readable in the
// bundle and in devtools, and it escapes the quotes and the # in the colours.
const ERASER_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(eraserCursorSvg)}") ` +
	`${toRenderedPx(ERASER_HOTSPOT.x)} ${toRenderedPx(ERASER_HOTSPOT.y)}, auto`;

/**
 * How far the pointer may travel between press and release and still count as a
 * click rather than a stroke. Covers hand jitter on a press, nothing more.
 */
export const ERASER_CLICK_SLOP_PX = 4;

/** Keeps an empty box grabbable with the Move tool. */
export const TEXT_MIN_WIDTH_PX = 24;
/** Room for the caret past the last glyph, so it is not clipped while typing. */
const TEXT_WIDTH_SLACK_PX = 4;

/** Below these, a click-without-drag is a misclick rather than a shape. */
const MIN_RECT_SIZE_PX = 3;
const MIN_ELLIPSE_RADIUS_PX = 2;

export interface ToolCanvasFlags {
	isDrawingMode: boolean;
	selection: boolean;
	skipTargetFind: boolean;
	defaultCursor: string;
	/**
	 * Fabric uses this instead of `defaultCursor` whenever `isDrawingMode` is
	 * on, so it is the one that actually shows for Pen and Eraser.
	 */
	freeDrawingCursor: string;
}

/** Per-gesture scratch state, held by the component so this module stays pure. */
export interface ToolGestureState {
	isDrawingShape: boolean;
	startX: number;
	startY: number;
	currentShape: FabricObject | null;
}

export const createToolGestureState = (): ToolGestureState => ({
	isDrawingShape: false,
	startX: 0,
	startY: 0,
	currentShape: null,
});

/**
 * The canvas surface the tools mutate. Structural rather than `Canvas` so the
 * spec can drive it: jsdom returns null from getContext('2d'), so a real Fabric
 * Canvas cannot be constructed in the harness. A real Canvas satisfies it.
 */
export interface ToolCanvas {
	add(...objects: FabricObject[]): unknown;
	remove(...objects: FabricObject[]): unknown;
	renderAll(): unknown;
}

/** The canvas properties a tool owns. A real Canvas satisfies it. */
export interface ToolFlagTarget {
	isDrawingMode: boolean;
	selection: boolean;
	skipTargetFind: boolean;
	defaultCursor: string;
	freeDrawingCursor: string;
}

export interface ToolPointer {
	x: number;
	y: number;
}

export interface ToolMouseDown {
	pointer: ToolPointer;
	/** The DOM event. Fabric needs it to place a caret at the click point. */
	source: TPointerEvent;
}

/** An existing text box the Text tool can put a caret into. */
export interface EditableText {
	isEditing: boolean;
}

export interface TextOrigin {
	left: number;
	top: number;
	/** Room to the canvas edge. The wrap point, not the box's own width. */
	maxWidth: number;
}

/**
 * The slice of Fabric's Textbox this module measures and resizes. Structural so
 * the spec can drive it: a real Textbox measures text while constructing and
 * throws without a 2D context, which jsdom has none of.
 */
export interface ResizableTextbox {
	left: number;
	width: number;
	set(key: 'width', value: number): unknown;
	initDimensions(): void;
	calcTextWidth(): number;
	setCoords(): unknown;
}

/** Everything a tool may touch, so no tool reaches back into the component. */
export interface ToolContext {
	canvas: ToolCanvas;
	gesture: ToolGestureState;
	/** Stroke colour, already converted to rgba by the component. */
	strokeColor: string;
	size: number;
	canvasWidth: number;
	canvasHeight: number;
	/**
	 * Textbox construction stays with the component: it measures text while
	 * constructing and throws without a 2D context, which the jsdom spec has
	 * none of.
	 */
	createText: (origin: TextOrigin) => void;
	/**
	 * Topmost existing text box under the pointer, or null.
	 *
	 * Deliberately text-only rather than Fabric's own hit test. Leaving
	 * `skipTargetFind` on means a click in Text mode can never select or start
	 * dragging a stroke or a shape, and it cannot be swallowed by a near-miss on
	 * something that is not text.
	 */
	findTextAt: (pointer: ToolPointer) => EditableText | null;
	/** Put the caret in an existing box at the click, entering editing if needed. */
	focusTextAt: (text: EditableText, source: TPointerEvent) => void;
	/** Remove a text box whole, as one history step. */
	removeText: (text: EditableText) => void;
	recordSnapshot: () => void;
}

export interface ToolDefinition {
	id: ToolId;
	flags: ToolCanvasFlags;
	/** Whether the pointer draws a size-preview ring on contextTop. */
	showsSizePreview: boolean;
	onMouseDown?: (ctx: ToolContext, event: ToolMouseDown) => void;
	onMouseMove?: (ctx: ToolContext, pointer: ToolPointer) => void;
	onMouseUp?: (ctx: ToolContext, pointer: ToolPointer) => void;
}

/**
 * Applied to every annotation the user can manipulate. `erasable` is what the
 * EraserBrush walks for; it ignores `evented` and `selectable` entirely.
 */
export const ANNOTATION_OBJECT_DEFAULTS = {
	selectable: true,
	evented: true,
	erasable: true,
} as const;

/**
 * Text is the one type the eraser removes whole rather than cutting into. Not
 * being erasable is what keeps it out of the brush's target list; the component
 * sweeps for touched text separately.
 */
export const TEXT_OBJECT_DEFAULTS = {
	selectable: true,
	evented: true,
	erasable: false,
} as const;

export const clampTextOrigin = (
	x: number,
	y: number,
	canvasWidth: number,
	canvasHeight: number,
): TextOrigin => {
	const left: number = Math.min(Math.max(x, 0), Math.max(canvasWidth - TEXT_EDGE_CLAMP_PX, 0));
	const top: number = Math.min(Math.max(y, 0), Math.max(canvasHeight - TEXT_EDGE_CLAMP_PX, 0));

	// Room to the canvas edge. The box only grows into this as the user types;
	// it does not start this wide.
	return { left, top, maxWidth: Math.max(canvasWidth - left, TEXT_MIN_WIDTH_PX) };
};

/**
 * Sizes a text box to its own content, capped at the canvas edge.
 *
 * A Textbox has a fixed width and wraps inside it, so sizing one to the whole
 * remaining canvas width makes every box run to the right edge no matter how
 * little is in it, which reads as broken the moment it is selected. Instead the
 * box hugs its text and stops growing at the edge, and only then does new text
 * wrap.
 *
 * Returns the width applied, so callers can assert one happened.
 */
export const fitTextboxWidth = (textbox: ResizableTextbox, canvasWidth: number): number => {
	const maxWidth: number = Math.max(canvasWidth - textbox.left, TEXT_MIN_WIDTH_PX);

	// Measure at the full available width first. Measuring at the current width
	// would only report the wrapping that width already causes, so the box could
	// shrink but never grow back.
	textbox.set('width', maxWidth);
	textbox.initDimensions();

	// calcTextWidth is the widest rendered line. Once the text is long enough to
	// wrap it lands at about maxWidth, so growth naturally stops at the edge.
	const used: number = Math.ceil(textbox.calcTextWidth()) + TEXT_WIDTH_SLACK_PX;
	const fitted: number = Math.min(maxWidth, Math.max(used, TEXT_MIN_WIDTH_PX));

	textbox.set('width', fitted);
	textbox.initDimensions();
	textbox.setCoords();

	return fitted;
};

export const createArrow = (
	x1: number,
	y1: number,
	x2: number,
	y2: number,
	strokeColor: string,
	strokeWidth: number,
): Group => {
	const angleRad: number = Math.atan2(y2 - y1, x2 - x1);
	const headSize: number = Math.max(12, strokeWidth * 3.5);

	// End the line at the base of the arrowhead so they join cleanly, and place
	// the head so its tip lands exactly on the pointer.
	const headCenterX: number = x2 - Math.cos(angleRad) * (headSize / 2);
	const headCenterY: number = y2 - Math.sin(angleRad) * (headSize / 2);

	const line = new Line([x1, y1, headCenterX, headCenterY], {
		stroke: strokeColor,
		strokeWidth,
		strokeLineCap: 'round',
		strokeUniform: true,
	});

	const head = new Triangle({
		left: headCenterX,
		top: headCenterY,
		originX: 'center',
		originY: 'center',
		width: headSize,
		height: headSize,
		fill: strokeColor,
		// Fabric's Triangle points up by default; rotate so the apex follows the
		// line direction.
		angle: (angleRad * 180 / Math.PI) + 90,
	});

	return new Group([line, head], {
		objectCaching: false,
		...ANNOTATION_OBJECT_DEFAULTS,
	});
};

const shapeStyle = (ctx: ToolContext) => ({
	fill: 'transparent',
	stroke: ctx.strokeColor,
	strokeWidth: ctx.size,
	strokeUniform: true,
	objectCaching: false,
	...ANNOTATION_OBJECT_DEFAULTS,
});

const startShape = (ctx: ToolContext, tool: ToolId, pointer: ToolPointer): void => {
	ctx.gesture.startX = pointer.x;
	ctx.gesture.startY = pointer.y;
	ctx.gesture.isDrawingShape = true;
	ctx.gesture.currentShape = null;

	if (tool === TOOL.SQUARE) {
		const rect = new Rect({
			left: pointer.x,
			top: pointer.y,
			width: 0,
			height: 0,
			...shapeStyle(ctx),
		});
		ctx.gesture.currentShape = rect;
		ctx.canvas.add(rect);
	} else if (tool === TOOL.CIRCLE) {
		const ellipse = new Ellipse({
			left: pointer.x,
			top: pointer.y,
			rx: 0,
			ry: 0,
			...shapeStyle(ctx),
		});
		ctx.gesture.currentShape = ellipse;
		ctx.canvas.add(ellipse);
	}
	// ARROW is built on the first drag move so the head can be oriented toward
	// the live pointer.
};

const resizeShape = (ctx: ToolContext, tool: ToolId, pointer: ToolPointer): void => {
	if (!ctx.gesture.isDrawingShape) return;

	if (tool === TOOL.ARROW) {
		// Rebuilt each move so the head tracks the pointer direction. These
		// intermediate objects must never reach history, which is why only
		// finishShape records.
		if (ctx.gesture.currentShape) {
			ctx.canvas.remove(ctx.gesture.currentShape);
		}
		const arrow: Group = createArrow(
			ctx.gesture.startX,
			ctx.gesture.startY,
			pointer.x,
			pointer.y,
			ctx.strokeColor,
			ctx.size,
		);
		ctx.gesture.currentShape = arrow;
		ctx.canvas.add(arrow);
		ctx.canvas.renderAll();
		return;
	}

	const shape: FabricObject | null = ctx.gesture.currentShape;
	if (!shape) return;

	const left: number = Math.min(ctx.gesture.startX, pointer.x);
	const top: number = Math.min(ctx.gesture.startY, pointer.y);
	const width: number = Math.abs(pointer.x - ctx.gesture.startX);
	const height: number = Math.abs(pointer.y - ctx.gesture.startY);

	if (tool === TOOL.SQUARE) {
		shape.set({ left, top, width, height });
	} else if (tool === TOOL.CIRCLE) {
		(shape as Ellipse).set({ left, top, rx: width / 2, ry: height / 2 });
	}
	ctx.canvas.renderAll();
};

/** Returns whether a shape was kept, so the caller can assert one action. */
const finishShape = (ctx: ToolContext, tool: ToolId): boolean => {
	const shape: FabricObject | null = ctx.gesture.currentShape;
	ctx.gesture.isDrawingShape = false;
	ctx.gesture.currentShape = null;

	if (!shape) return false;

	if (tool !== TOOL.ARROW && isTooSmall(shape, tool)) {
		ctx.canvas.remove(shape);
		ctx.canvas.renderAll();
		return false;
	}

	shape.setCoords();
	ctx.canvas.renderAll();
	ctx.recordSnapshot();
	return true;
};

const isTooSmall = (shape: FabricObject, tool: ToolId): boolean => {
	if (tool === TOOL.SQUARE) {
		return shape.width < MIN_RECT_SIZE_PX || shape.height < MIN_RECT_SIZE_PX;
	}
	if (tool === TOOL.CIRCLE) {
		const ellipse = shape as Ellipse;
		return ellipse.rx < MIN_ELLIPSE_RADIUS_PX || ellipse.ry < MIN_ELLIPSE_RADIUS_PX;
	}
	return false;
};

/**
 * Whether a press and release were a click rather than a stroke.
 *
 * The eraser brush itself already ignores a click: it fires `cancel` instead of
 * `end` below two points, so no pixels are erased. This is the same distinction
 * applied to whole-object text removal.
 */
export const isEraserClick = (gesture: ToolGestureState, pointer: ToolPointer): boolean => {
	return Math.abs(pointer.x - gesture.startX) <= ERASER_CLICK_SLOP_PX &&
		Math.abs(pointer.y - gesture.startY) <= ERASER_CLICK_SLOP_PX;
};

const shapeTool = (id: ToolId): ToolDefinition => ({
	id,
	flags: {
		isDrawingMode: false,
		selection: false,
		// Hit-testing off is what stops a click-drag from grabbing an existing
		// annotation instead of starting a new shape.
		skipTargetFind: true,
		defaultCursor: 'crosshair',
		freeDrawingCursor: 'crosshair',
	},
	showsSizePreview: false,
	onMouseDown: (ctx, event) => startShape(ctx, id, event.pointer),
	onMouseMove: (ctx, pointer) => resizeShape(ctx, id, pointer),
	onMouseUp: (ctx) => {
		finishShape(ctx, id);
	},
});

export const TOOLS: Record<ToolId, ToolDefinition> = {
	// The only tool that hit-tests. All selecting, moving, resizing, rotating
	// and double-click-to-edit-text lives here and nowhere else.
	[TOOL.MOVE]: {
		id: TOOL.MOVE,
		flags: {
			isDrawingMode: false,
			selection: true,
			skipTargetFind: false,
			defaultCursor: 'default',
			freeDrawingCursor: 'crosshair',
		},
		showsSizePreview: false,
	},
	[TOOL.PEN]: {
		id: TOOL.PEN,
		flags: {
			isDrawingMode: true,
			selection: false,
			skipTargetFind: true,
			defaultCursor: 'none',
			freeDrawingCursor: 'crosshair',
		},
		showsSizePreview: true,
	},
	// Creates unless the click lands on an existing text box, in which case it
	// puts the caret there. The hit test is text-only, so the near-miss problem
	// the old behaviour had (a hit-test over everything, suppressing creation
	// only when it happened to find an i-text) does not come back with it.
	[TOOL.TEXT]: {
		id: TOOL.TEXT,
		flags: {
			isDrawingMode: false,
			selection: false,
			skipTargetFind: true,
			defaultCursor: 'text',
			freeDrawingCursor: 'crosshair',
		},
		showsSizePreview: false,
		onMouseDown: (ctx, event) => {
			// An existing box takes the click: clicking inside the box you are
			// already typing in moves the caret there, and clicking a different
			// box starts editing it at that point. Only a click on empty canvas,
			// a stroke or a shape creates a new box.
			const existing: EditableText | null = ctx.findTextAt(event.pointer);
			if (existing) {
				ctx.focusTextAt(existing, event.source);
				return;
			}

			ctx.createText(clampTextOrigin(
				event.pointer.x,
				event.pointer.y,
				ctx.canvasWidth,
				ctx.canvasHeight,
			));
		},
	},
	[TOOL.SQUARE]: shapeTool(TOOL.SQUARE),
	[TOOL.CIRCLE]: shapeTool(TOOL.CIRCLE),
	[TOOL.ARROW]: shapeTool(TOOL.ARROW),
	// Shares the pen's size control and pointer preview; the component swaps in
	// the EraserBrush rather than the pencil.
	//
	// Text is not erasable, so the brush never touches it and removing a box is
	// a separate step. That step is bound to a click rather than a stroke:
	// dragging across a label should rub out what is behind it and leave the
	// label alone, so only a deliberate click on a box removes it.
	[TOOL.ERASER]: {
		id: TOOL.ERASER,
		flags: {
			isDrawingMode: true,
			selection: false,
			skipTargetFind: true,
			defaultCursor: 'none',
			freeDrawingCursor: ERASER_CURSOR,
		},
		showsSizePreview: true,
		onMouseDown: (ctx, event) => {
			ctx.gesture.startX = event.pointer.x;
			ctx.gesture.startY = event.pointer.y;
		},
		onMouseUp: (ctx, pointer) => {
			if (!isEraserClick(ctx.gesture, pointer)) return;

			const text: EditableText | null = ctx.findTextAt(pointer);
			if (!text) return;

			ctx.removeText(text);
		},
	},
};

export const getTool = (tool: ToolId): ToolDefinition => TOOLS[tool];

export const applyToolToCanvas = (canvas: ToolFlagTarget, tool: ToolId): void => {
	const { flags } = TOOLS[tool];
	canvas.isDrawingMode = flags.isDrawingMode;
	canvas.selection = flags.selection;
	canvas.skipTargetFind = flags.skipTargetFind;
	canvas.defaultCursor = flags.defaultCursor;
	canvas.freeDrawingCursor = flags.freeDrawingCursor;
};
